import React from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { useMeasure } from 'react-use';

interface Props {
  /**
   * A class to apply to the children container
   */
  className?: string;

  /**
   * A node to (invisibly) render in order to determine the size to lock to
   * This should be the largest possible content for the container
   */
  sizer: React.ReactNode;

  /**
   * The actual content to display after calculating the width
   */
  children: React.ReactNode;
}

/**
 * A component used to force the width of a div to equal the width of the `sizer` passed in
 * Used when the width is difficult to precompute
 * Example usage: prevent a div containing a string representing pixel outout ('1600px') from shrinking
 * if the string becomes '999px' or '100%'
 * @author Rosalyn Rowe
 */
const SizedContainer: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  sizer,
  children
}) => {
  const cx = useClassNameMapper();
  const [containerRef, { width }] = useMeasure();

  if (width) {
    return (
      <div className={className} style={{ width }}>
        {children}
      </div>
    );
  }

  return (
    <div className={cx('invisible')} ref={containerRef}>
      {sizer}
    </div>
  );
};

export default SizedContainer;
